/**
 * Decodes HTML entities in a string.
 * This is used after sanitizing HTML to convert entities like &amp; to & for display.
 * @param {string} encodedString - String potentially containing HTML entities
 * @returns {string} String with entities decoded
 */
export function decodeHtmlEntities(encodedString) {
  const textArea = document.createElement("textarea");
  textArea.innerHTML = encodedString;
  return textArea.value;
}

/**
 * Formats navigation text based on the visual override type.
 * Prepends "Back to" for backward navigation or "Go to" for forward navigation.
 * @param {Object} intl - react-intl intl object for formatting messages
 * @param {string} visualOverride - The visual override value (e.g., "Next", "Back", "Up", "Down")
 * @param {string} tooltipText - The tooltip text to prepend to
 * @returns {string} Formatted navigation text
 */
export function formatNavigationText(intl, visualOverride, tooltipText) {
  const navigationHotspots = ["Next", "Back", "Down", "Up"];
  const isNavigationHotspot = navigationHotspots.includes(visualOverride);

  if (!isNavigationHotspot || !tooltipText) {
    return tooltipText;
  }

  // If tooltip text is the same as navigation direction (case-insensitive),
  // don't prepend navigation text to avoid redundancy like "Back to back"
  if (tooltipText.toLowerCase() === visualOverride.toLowerCase()) {
    return visualOverride;
  }

  const isBackNavigation = ["Back", "Up"].includes(visualOverride);

  const navigationText = isBackNavigation
    ? intl.formatMessage({
        id: "backToView",
        defaultMessage: "Back to",
      })
    : intl.formatMessage({
        id: "goToView",
        defaultMessage: "Go to",
      });

  return `${navigationText} ${tooltipText}`;
}

/**
 * Converts superscript and subscript HTML tags to screen-reader-friendly text.
 * This ensures that content like "A<sub>e</sub>" is announced as "A subscript e"
 * and "x<sup>2</sup>" is announced as "x superscript 2" by screen readers.
 * @param {string} htmlString - String potentially containing <sup> and <sub> tags
 * @param {Object} intl - react-intl intl object for translations
 * @returns {string} String with sup/sub tags converted to readable text
 */
export function convertSupSubToReadableText(htmlString, intl) {
  if (!htmlString) return htmlString;

  const superscriptText = intl
    ? intl.formatMessage({
        id: "superscript",
        defaultMessage: " superscript ",
      })
    : " superscript ";

  const subscriptText = intl
    ? intl.formatMessage({
        id: "subscript",
        defaultMessage: " subscript ",
      })
    : " subscript ";

  let result = htmlString;

  result = result.replace(/<sup[^>]*>(.*?)<\/sup>/gi, `${superscriptText}$1`);
  result = result.replace(/<sub[^>]*>(.*?)<\/sub>/gi, `${subscriptText}$1`);

  return result;
}

/**
 * Returns react-intl formatters that convert sup/sub tags to screen-reader-friendly text.
 * This handles both HTML tags (via convertSupSubToReadableText) and ICU message format
 * tags (via these formatters).
 *
 * Example usage with intl.formatMessage():
 * - HTML format: "<sup>2</sup>" → " superscript 2"
 * - ICU format: "{sup}2{/sup}" → " superscript 2"
 *
 * @param {Object} intl - react-intl intl object for translations
 * @returns {Object} Formatters object for use with intl.formatMessage()
 */
export function getScreenReaderFormatters(intl) {
  const superscriptText = intl
    ? intl.formatMessage({
        id: "superscript",
        defaultMessage: " superscript ",
      })
    : " superscript ";

  const subscriptText = intl
    ? intl.formatMessage({
        id: "subscript",
        defaultMessage: " subscript ",
      })
    : " subscript ";

  return {
    sup: (chunks) =>
      `${superscriptText}${Array.isArray(chunks) ? chunks.join("") : chunks}`,
    sub: (chunks) =>
      `${subscriptText}${Array.isArray(chunks) ? chunks.join("") : chunks}`,
  };
}
